import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Type } from "@themes/pollinator/ui/Type";
import { PollinatorLogo } from "@themes/pollinator/ui/PollinatorLogo";
import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";
import { usePlatformStore } from "../store";
import { useIsLoginDisabled } from "../hooks/useIsLoginDisabled";
import { useMarketingTracking } from "@platform/features/marketing";
import { platformFooterItems } from "./platformFooterItems";

/** The 16px on a 24px line that the footer's links and copyright are set in. */
const FOOTER_TEXT = "text-[16px] leading-6 md:text-[16px] md:leading-6";

export function PlatformAppFooter() {
  const currentYear = new Date().getFullYear();
  const { trackCtaClick } = useMarketingTracking();
  const { pathname, hash } = useLocation();
  const isSupportPageDisabled = usePlatformStore(
    (s) => s.featureFlags.isSupportPageDisabled,
  );
  const user = usePlatformStore((s) => s.user);
  const { isLoginDisabled } = useIsLoginDisabled();

  // Filter out Support link when support page is disabled. Support is the
  // dashboard's, behind signing in; while login is closed a visitor who is
  // not signed in would be sent through the closed login to the homepage, so
  // their Support is the contact form instead.
  const filteredFooterItems = useMemo(() => {
    if (isSupportPageDisabled) {
      return platformFooterItems.map((section) => ({
        ...section,
        links: section.links.filter((link) => link.url !== "/support"),
      }));
    }
    if (!user && isLoginDisabled) {
      return platformFooterItems.map((section) => ({
        ...section,
        links: section.links.map((link) =>
          link.url === "/support" ? { ...link, url: "/contact" } : link
        ),
      }));
    }
    return platformFooterItems;
  }, [isSupportPageDisabled, user, isLoginDisabled]);

  // A link to a section of the homepage scrolls there as the header's do. The
  // address changing is what scrolls, so a link to the section the address
  // already names would change nothing, and scrolls by hand.
  const scrollIfAlreadyThere = (event: React.MouseEvent, url: string) => {
    const [path, anchor] = url.split("#");
    if (!anchor || path !== "/" || pathname !== "/" || hash !== `#${anchor}`) return;
    event.preventDefault();
    document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    // One footer for the marketing site, the dashboard and the video pages,
    // laid out as the marketing design draws it at each width: 48px above and
    // below, the page's 16px gutter until a desktop, and the logo with the
    // copyright under it until there is room to set them side by side.
    <OuterContentContainer
      as="footer"
      className="border-t border-brand-800 py-12 md:py-12"
      innerClassName="md:px-4 lg:px-8"
    >
      <div className="flex flex-col gap-[42px] w-full lg:gap-[58px]">
        {/* Three columns on a phone, with the fourth wrapping under them. */}
        <div className="grid grid-cols-3 gap-8 md:grid-cols-4">
          {filteredFooterItems.map((section, sectionIndex) => (
            <div key={sectionIndex} className="flex flex-col gap-2.5">
              <Type
                variant="caption"
                className="text-[14px] leading-5 font-bold md:text-[14px] md:leading-5"
              >
                {section.title}
              </Type>
              <div className="flex flex-col">
                {section.links.map((link, linkIndex) => {
                  return (
                    <Type
                      as={link.isExternal ? "a" : Link}
                      key={linkIndex}
                      href={link.url}
                      to={link.url}
                      target={link.isExternal ? "_blank" : "_self"}
                      // A link to a form counts as a call to action for it,
                      // so somebody who reaches the form this way is told
                      // from somebody who followed a link from outside.
                      onClick={(event: React.MouseEvent) => {
                        if (link.formSlug) {
                          trackCtaClick(`footer_${link.title.toLowerCase()}`, {
                            form_slug: link.formSlug,
                          });
                        }
                        scrollIfAlreadyThere(event, link.url);
                      }}
                      variant="caption"
                      className={`${FOOTER_TEXT} font-medium hover:text-brand-700`}
                      data-testid={`platform-footer-${section.title
                        .toLowerCase()
                        .replace(/\s+/g, "-")}-${link.title
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    >
                      {link.title}
                    </Type>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col items-start gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-col items-start justify-center gap-2">
            <PollinatorLogo size="sm" />
            <Type variant="caption" className="italic">
              Pollinator is made by artists, for artists. Help us shape a new
              social medium.
            </Type>
          </div>
          <Type variant="caption" className={FOOTER_TEXT}>
            © {currentYear} Pollinator
          </Type>
        </div>
      </div>
    </OuterContentContainer>
  );
}

export default PlatformAppFooter;
